import {SubscriptionEvent} from '../api/generated';
import React, {useState} from 'react';
import {SubscriptionKeysUpdate} from '../types';
import dot from 'dot-object';
import {isDefined} from '../lib/utils';
import {formatIntAmount} from '../lib/formaters';
import {Button, Typography} from 'antd';

const {Text} = Typography;

const descriptionComponentMapping: Record<string, (value: string) => JSX.Element | string> = {
  [SubscriptionKeysUpdate.PAUSED_AT_PERIOD_END]: (value) =>
    value ? <>Paused at period end</> : <>Subscription resumed</>,
  [SubscriptionKeysUpdate.CANCELED_AT_PERIOD_END]: (value) =>
    value ? <>Canceled at period end</> : <>Subscription resumed</>,
  [SubscriptionKeysUpdate.AMOUNT]: (value) => (
    <>
      Amount changed to <strong>{formatIntAmount(+value)}</strong>
    </>
  ),
  [SubscriptionKeysUpdate.INTERVAL_COUNT]: (value) => (
    <>
      Interval count changed to <strong>{value}</strong>
    </>
  ),

  [SubscriptionKeysUpdate.CUSTOMER_EMAIL]: (value) =>
    value ? (
      <>
        Customer email changed to <strong>{value}</strong>
      </>
    ) : (
      <>Customer email removed</>
    ),
  [SubscriptionKeysUpdate.CUSTOMER_NAME]: (value) =>
    value ? (
      <>
        Customer name changed to <strong>{value}</strong>
      </>
    ) : (
      <>Customer name removed</>
    ),
  [SubscriptionKeysUpdate.CUSTOMER_PHONE]: (value) =>
    value ? (
      <>
        Customer phone changed to <strong>{value}</strong>
      </>
    ) : (
      <>Customer phone removed</>
    ),
  [SubscriptionKeysUpdate.BILLING_DETAILS_EMAIL]: (value) =>
    value ? (
      <>
        Billing details, email changed to <strong>{value}</strong>
      </>
    ) : (
      <>Billing details email removed</>
    ),
  [SubscriptionKeysUpdate.BILLING_DETAILS_NAME]: (value) =>
    value ? (
      <>
        Billing details, name changed to <strong>{value}</strong>
      </>
    ) : (
      <>Billing details name removed</>
    ),
  [SubscriptionKeysUpdate.BILLING_DETAILS_COMPANY]: (value) =>
    value ? (
      <>
        Billing details, company changed to <strong>{value}</strong>
      </>
    ) : (
      <>Billing details company removed</>
    ),
  [SubscriptionKeysUpdate.BILLING_DETAILS_PHONE]: (value) =>
    value ? (
      <>
        Billing details, phone changed to <strong>{value}</strong>
      </>
    ) : (
      <>Billing details phone removed</>
    ),
  [SubscriptionKeysUpdate.BILLING_DETAILS_ADDRESS_CITY]: (value) =>
    value ? (
      <>
        Billing details, address city changed to <strong>{value}</strong>
      </>
    ) : (
      <>Billing details address city removed</>
    ),
  [SubscriptionKeysUpdate.BILLING_DETAILS_ADDRESS_COUNTRY]: (value) =>
    value ? (
      <>
        Billing details, address country changed to <strong>{value}</strong>
      </>
    ) : (
      <>Billing details address country removed</>
    ),
  [SubscriptionKeysUpdate.BILLING_DETAILS_ADDRESS_LINE1]: (value) =>
    value ? (
      <>
        Billing details, address line 1 changed to <strong>{value}</strong>
      </>
    ) : (
      <>Billing details address line 1 removed</>
    ),
  [SubscriptionKeysUpdate.BILLING_DETAILS_ADDRESS_LINE2]: (value) =>
    value ? (
      <>
        Billing details, address line 2 changed to <strong>{value}</strong>
      </>
    ) : (
      <>Billing details address line 2 removed</>
    ),
  [SubscriptionKeysUpdate.BILLING_DETAILS_ADDRESS_ZIP]: (value) =>
    value ? (
      <>
        Billing details, address zip changed to <strong>{value}</strong>
      </>
    ) : (
      <>Billing details address zip removed</>
    ),
  [SubscriptionKeysUpdate.BILLING_DETAILS_ADDRESS_STATE]: (value) =>
    value ? (
      <>
        Billing details, address state changed to <strong>{value}</strong>
      </>
    ) : (
      <>Billing details address state removed</>
    ),
  [SubscriptionKeysUpdate.SHIPPING_DETAILS_EMAIL]: (value) =>
    value ? (
      <>
        Shipping details, email changed to <strong>{value}</strong>
      </>
    ) : (
      <>Shipping details email removed</>
    ),
  [SubscriptionKeysUpdate.SHIPPING_DETAILS_NAME]: (value) =>
    value ? (
      <>
        Shipping details, name changed to <strong>{value}</strong>
      </>
    ) : (
      <>Shipping details name removed</>
    ),
  [SubscriptionKeysUpdate.SHIPPING_DETAILS_COMPANY]: (value) =>
    value ? (
      <>
        Shipping details, company changed to <strong>{value}</strong>
      </>
    ) : (
      <>Shipping details company removed</>
    ),
  [SubscriptionKeysUpdate.SHIPPING_DETAILS_PHONE]: (value) =>
    value ? (
      <>
        Shipping details, phone changed to <strong>{value}</strong>
      </>
    ) : (
      <>Shipping details removed</>
    ),
  [SubscriptionKeysUpdate.SHIPPING_DETAILS_ADDRESS_CITY]: (value) =>
    value ? (
      <>
        Shipping details, address city changed to <strong>{value}</strong>
      </>
    ) : (
      <>Shipping details address city removed</>
    ),
  [SubscriptionKeysUpdate.SHIPPING_DETAILS_ADDRESS_COUNTRY]: (value) =>
    value ? (
      <>
        Shipping details, address country changed to <strong>{value}</strong>
      </>
    ) : (
      <>Shipping details address country removed</>
    ),
  [SubscriptionKeysUpdate.SHIPPING_DETAILS_ADDRESS_LINE1]: (value) =>
    value ? (
      <>
        Shipping details, address line 1 changed to <strong>{value}</strong>
      </>
    ) : (
      <>Shipping details address line 1 removed</>
    ),
  [SubscriptionKeysUpdate.SHIPPING_DETAILS_ADDRESS_LINE2]: (value) =>
    value ? (
      <>
        Shipping details, address line 2 changed to <strong>{value}</strong>
      </>
    ) : (
      <>Shipping details address line 2 removed</>
    ),
  [SubscriptionKeysUpdate.SHIPPING_DETAILS_ADDRESS_ZIP]: (value) =>
    value ? (
      <>
        Shipping details, address zip changed to <strong>{value}</strong>
      </>
    ) : (
      <>Shipping details address zip removed</>
    ),
  [SubscriptionKeysUpdate.SHIPPING_DETAILS_ADDRESS_STATE]: (value) =>
    value ? (
      <>
        Shipping details, address state changed to <strong>{value}</strong>
      </>
    ) : (
      <>Shipping details address state removed</>
    )
};

type TimelineSubscriptionMessageProps = {
  event: SubscriptionEvent & {changed: string[]};
};

export const TimelineSubscriptionMessage: React.FC<TimelineSubscriptionMessageProps> = ({
  event
}): JSX.Element => {
  const [sliceCount, setSliceCount] = useState(3);
  const description = event.changed
    ?.map((change) => {
      const value = dot.pick(change, event.object);

      if (Array.isArray(value)) {
        return value[0] ? descriptionComponentMapping[change]?.(value[0]) : undefined;
      } else {
        return descriptionComponentMapping[change]?.(value);
      }
    })
    .filter((value) => isDefined(value));

  const sliceCountDiff = description.length - sliceCount;

  return description?.length ? (
    <Text type="secondary" style={{fontSize: '12px'}}>
      {description.slice(0, sliceCount).map((item) => (
        <span style={{display: 'block'}}>{item}</span>
      ))}
      {description.length > sliceCount ? (
        <Button
          size="small"
          type="link"
          style={{display: 'block', fontSize: '12px'}}
          onClick={() => setSliceCount(99)}>
          ...<>{sliceCountDiff} more</>
        </Button>
      ) : null}
    </Text>
  ) : (
    <></>
  );
};
